/**
 * Thứ tự hiển thị máy vò trên BẢNG MÁY TRẠM SẢN XUẤT (khác màn quản lý, vốn
 * xếp cứng M1 → M36).
 *
 * Người đứng xưởng chỉ quan tâm "máy nào sắp phải ra chè" nên máy sắp xong phải
 * nằm ngay đầu bảng, không phải dò giữa 36 dòng.
 *
 * Thứ tự: đang chạy (còn ít phút nhất trước) → dừng → mất kết nối → lỗi; trong
 * cùng nhóm thì theo số máy để mắt còn bám được vị trí quen thuộc.
 */

import { computeElapsed } from './dead-reckoning'
import { machineIndex } from './sort'
import type { MachineStatus, TeaMachine } from './types'

/** Nhóm ưu tiên theo trạng thái (nhỏ hơn = lên trên). */
const STATUS_RANK: Record<MachineStatus, number> = {
    running: 0,
    stopped: 1,
    offline: 2,
    error: 3,
}

/**
 * Số giây còn lại tới lúc máy dừng. Máy không chạy hoặc chưa cài giờ trả
 * `Infinity` — không có mốc kết thúc nên không tranh chỗ với máy đang chạy.
 */
export function remainingSeconds(machine: TeaMachine, nowMs: number): number {
    if (machine.status !== 'running' || machine.configuredMin <= 0) {
        return Number.POSITIVE_INFINITY
    }
    const { elapsedSec } = computeElapsed(machine, nowMs)
    return Math.max(0, machine.configuredMin * 60 - elapsedSec)
}

/** So sánh 2 máy theo thứ tự bảng máy trạm. */
export function compareForBoard(
    a: TeaMachine,
    b: TeaMachine,
    nowMs: number,
): number {
    const rank = STATUS_RANK[a.status] - STATUS_RANK[b.status]
    if (rank !== 0) return rank
    const ra = remainingSeconds(a, nowMs)
    const rb = remainingSeconds(b, nowMs)
    // Cả hai cùng vô hạn (máy dừng, hoặc đang chạy mà chưa cài giờ) → về số máy.
    if (ra !== rb && Number.isFinite(Math.min(ra, rb))) return ra - rb
    return machineIndex(a.code) - machineIndex(b.code)
}

/** Bản sao đã sắp xếp — KHÔNG sửa mảng gốc. */
export function sortMachinesForBoard(
    machines: TeaMachine[],
    nowMs: number,
): TeaMachine[] {
    return [...machines].sort((a, b) => compareForBoard(a, b, nowMs))
}
